import React, { useCallback, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import { getFirebaseAuthToken } from "../../../helpers/firebase_helper";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const fmt = (n) => (n ?? 0).toLocaleString();
const pct = (x) => `${((x || 0) * 100).toFixed(1)}%`;
const ago = (ms) => {
  if (!ms) return "never";
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString();
};

const Stat = ({ label, value, sub, tone }) => (
  <div className="col-6 col-md-3">
    <div className="border rounded p-3 h-100">
      <div className="text-muted small">{label}</div>
      <div className={`fs-4 fw-semibold ${tone ? `text-${tone}` : ""}`}>
        {value}
      </div>
      {sub && <div className="text-muted small">{sub}</div>}
    </div>
  </div>
);

/**
 * How much a partner is using the API: calls per day (split into successful
 * and failed), totals, and a breakdown per endpoint. Reads the daily
 * counters the server keeps per partner (partner_usage).
 */
const PartnerUsage = ({ partnerId }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/partners/${partnerId}/usage?days=${days}`,
        { headers: { Authorization: `${await getFirebaseAuthToken()}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to load usage");
      setData(json);
    } catch (e) {
      Swal.fire("Could not load usage", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [partnerId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const chart = data && {
    series: [
      {
        name: "Successful",
        data: data.series.map((d) => d.calls - d.errors),
      },
      { name: "Failed", data: data.series.map((d) => d.errors) },
    ],
    options: {
      chart: { type: "bar", stacked: true, toolbar: { show: false } },
      colors: ["#0ab39c", "#f06548"],
      plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.series.map((d) => d.day.slice(5)),
        labels: { rotate: -45, style: { fontSize: "10px" } },
      },
      yaxis: { labels: { formatter: (v) => Math.round(v) } },
      legend: { position: "top", horizontalAlign: "right" },
      tooltip: { shared: true, intersect: false },
    },
  };

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-0">API usage</h4>
          <p className="text-muted mb-0 mt-1">
            Every call this partner made with its API key, by day and by
            endpoint.
          </p>
        </div>
        <ButtonGroup size="sm">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              color={days === d ? "primary" : "light"}
              onClick={() => setDays(d)}
            >
              {d} days
            </Button>
          ))}
        </ButtonGroup>
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <div style={{ opacity: loading ? 0.6 : 1 }}>
            <div className="row g-3 mb-3">
              <Stat
                label="Calls"
                value={fmt(t.calls)}
                sub={`last ${days} days`}
              />
              <Stat
                label="Error rate"
                value={pct(t.errorRate)}
                sub={`${fmt(t.errors)} failed · ${fmt(t.serverErrors)} server`}
                tone={
                  t.serverErrors > 0
                    ? "danger"
                    : t.errorRate > 0.05
                      ? "warning"
                      : undefined
                }
              />
              <Stat label="Avg response" value={`${fmt(t.avgMs)} ms`} />
              <Stat label="Last call" value={ago(t.lastCallAt)} />
            </div>

            {t.calls === 0 ? (
              <p className="text-muted mb-0">No calls in this period.</p>
            ) : (
              <>
                <ReactApexChart
                  type="bar"
                  height={260}
                  series={chart.series}
                  options={chart.options}
                />
                <div className="table-responsive mt-3">
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Endpoint</th>
                        <th className="text-end">Calls</th>
                        <th className="text-end">Failed</th>
                        <th className="text-end">Server errors</th>
                        <th className="text-end">Avg ms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byRoute.map((r) => (
                        <tr key={r.route}>
                          <td>
                            <code>{r.route}</code>
                          </td>
                          <td className="text-end">{fmt(r.calls)}</td>
                          <td className="text-end">
                            {r.errors ? fmt(r.errors) : "—"}
                          </td>
                          <td
                            className={`text-end ${r.serverErrors ? "text-danger fw-semibold" : ""}`}
                          >
                            {r.serverErrors ? fmt(r.serverErrors) : "—"}
                          </td>
                          <td className="text-end">{fmt(r.avgMs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <p className="text-muted small mt-2 mb-0">
                  Failed = any 4xx or 5xx. 4xx are usually the partner's
                  requests (bad data, unknown member); server errors (5xx) are
                  ours to look at.
                </p>
              </>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default PartnerUsage;
